import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import PromotionsService from "services/PromotionsService";

const initialState = {
  promotions: [],
  promotion: null,
  loading: false,
  error: null,
};

/* ======================================================
   ASYNC THUNKS
====================================================== */

/* 🔹 Get all promotions */
export const getPromotions = createAsyncThunk(
  "promotions/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await PromotionsService.getPromotions();
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

/* 🔹 Get promotion by ID */
export const getPromotionById = createAsyncThunk(
  "promotions/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await PromotionsService.getPromotionById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

/* 🔹 Create promotion */
export const createPromotion = createAsyncThunk(
  "promotions/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await PromotionsService.createPromotion(data);
      return response.data.promotion;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

/* 🔹 Update promotion */
export const updatePromotionById = createAsyncThunk(
  "promotions/updateById",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await PromotionsService.updatePromotion(id, data);
      return response.data.promotion;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

/* 🔹 Delete promotion */
export const deletePromotionById = createAsyncThunk(
  "promotions/deleteById",
  async (id, { rejectWithValue }) => {
    try {
      await PromotionsService.deletePromotionById(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Error"
      );
    }
  }
);

/* ======================================================
   SLICE
====================================================== */

const promotionsSlice = createSlice({
  name: "promotions",
  initialState,
  reducers: {
    setPromotions: (state, { payload }) => {
      state.promotions = payload;
    },
    setPromotion: (state, { payload }) => {
      state.promotion = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
  },
  extraReducers: (builder) => {
    builder

      /* ---------- GET ALL ---------- */
      .addCase(getPromotions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPromotions.fulfilled, (state, { payload }) => {
        state.promotions = payload;
        state.loading = false;
      })
      .addCase(getPromotions.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      /* ---------- GET BY ID ---------- */
      .addCase(getPromotionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPromotionById.fulfilled, (state, { payload }) => {
        state.promotion = payload;
        state.loading = false;
      })
      .addCase(getPromotionById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      /* ---------- CREATE ---------- */
      .addCase(createPromotion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPromotion.fulfilled, (state, { payload }) => {
        state.promotions.push(payload);
        state.loading = false;
      })
      .addCase(createPromotion.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      /* ---------- UPDATE ---------- */
      .addCase(updatePromotionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePromotionById.fulfilled, (state, { payload }) => {
        state.promotions = state.promotions.map((promotion) =>
          promotion._id === payload._id ? payload : promotion
        );
        state.loading = false;
      })
      .addCase(updatePromotionById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      /* ---------- DELETE ---------- */
      .addCase(deletePromotionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePromotionById.fulfilled, (state, { payload }) => {
        state.promotions = state.promotions.filter(
          (promotion) => promotion._id !== payload
        );
        state.loading = false;
      })
      .addCase(deletePromotionById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      });
  },
});

export const {
  setPromotions,
  setPromotion,
  setLoading,
  setError,
} = promotionsSlice.actions;

export default promotionsSlice.reducer;
